// Google Places Autocomplete service for hero and dashboard search.
// Supplements the Geocoding API with address predictions from the
// Places JS API. Falls back gracefully if Places is unavailable.

let autocompleteService = null;
let placesServiceInstance = null;
let sessionToken = null;
let loadPromise = null;
let placesAvailable = null; // null = unknown, true/false after first attempt

/**
 * Load the Google Maps JS API with the Places library.
 * Uses a global callback so we know exactly when the API is ready.
 * Returns true if Places loaded successfully, false otherwise.
 */
async function ensurePlacesLoaded() {
  // Already determined availability
  if (placesAvailable === true) return true;
  if (placesAvailable === false) return false;

  // Already loading - wait for result
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      // Scenario 1: Places already loaded (e.g. another component loaded it)
      if (window.google?.maps?.places?.AutocompleteService) {
        placesAvailable = true;
        return true;
      }

      // Scenario 2: Maps JS loaded but no Places - dynamic import
      if (window.google?.maps?.importLibrary) {
        await window.google.maps.importLibrary("places");
        placesAvailable = !!window.google?.maps?.places?.AutocompleteService;
        return placesAvailable;
      }

      // Scenario 3: Nothing loaded - inject script with callback
      const existing = document.getElementById("google-map-script");
      if (existing) {
        // Script tag exists (maybe @react-google-maps/api created it)
        // Wait for it then try to import places
        await new Promise((resolve) => {
          if (window.google?.maps) {
            resolve();
          } else {
            existing.addEventListener("load", resolve, { once: true });
            // Safety timeout in case load already fired
            setTimeout(resolve, 3000);
          }
        });
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary("places");
        }
        placesAvailable = !!window.google?.maps?.places?.AutocompleteService;
        return placesAvailable;
      }

      // No script at all - load fresh with callback for reliability
      await new Promise((resolve, reject) => {
        const cbName = "__placesReady_" + Date.now();
        window[cbName] = () => {
          delete window[cbName];
          resolve();
        };
        const script = document.createElement("script");
        script.id = "google-map-script";
        script.src =
          "https://maps.googleapis.com/maps/api/js" +
          `?key=${process.env.REACT_APP_GOOGLE_API_KEY}` +
          "&libraries=places" +
          `&callback=${cbName}`;
        script.async = true;
        script.onerror = () => {
          delete window[cbName];
          reject(new Error("Script load failed"));
        };
        document.head.appendChild(script);
      });

      placesAvailable = !!window.google?.maps?.places?.AutocompleteService;
      return placesAvailable;
    } catch (error) {
      console.warn("Places API not available:", error.message);
      placesAvailable = false;
      return false;
    }
  })();

  const result = await loadPromise;
  loadPromise = null;
  return result;
}

function getSessionToken() {
  if (!sessionToken) {
    sessionToken = new window.google.maps.places.AutocompleteSessionToken();
  }
  return sessionToken;
}

function resetSessionToken() {
  sessionToken = null;
}

function getAutocompleteServiceInstance() {
  if (!autocompleteService) {
    autocompleteService =
      new window.google.maps.places.AutocompleteService();
  }
  return autocompleteService;
}

function getPlacesServiceInstance() {
  if (!placesServiceInstance) {
    const div = document.createElement("div");
    placesServiceInstance = new window.google.maps.places.PlacesService(div);
  }
  return placesServiceInstance;
}

/**
 * Get autocomplete predictions for address search.
 * Returns [] if Places API is unavailable (graceful fallback).
 */
export async function getAutocompletePredictions(input, options = {}) {
  try {
    const loaded = await ensurePlacesLoaded();
    if (!loaded) return [];

    const service = getAutocompleteServiceInstance();

    return new Promise((resolve) => {
      service.getPlacePredictions(
        {
          input,
          types: options.types || ["geocode"],
          componentRestrictions:
            options.componentRestrictions || { country: "us" },
          sessionToken: getSessionToken(),
        },
        (results, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            resolve(results);
          } else {
            resolve([]);
          }
        }
      );
    });
  } catch (error) {
    console.warn("Places autocomplete failed:", error.message);
    return [];
  }
}

/**
 * Start loading the Places library in the background.
 * Call on component mount so the script is ready when the user starts typing.
 */
export function preloadPlaces() {
  ensurePlacesLoaded();
}

/**
 * Get full place details by place_id.
 * Converts to Geocoding API result shape so handleItemSelection works unchanged.
 */
export async function getPlaceDetails(placeId) {
  try {
    const loaded = await ensurePlacesLoaded();
    if (!loaded) return null;

    const service = getPlacesServiceInstance();

    return new Promise((resolve, reject) => {
      service.getDetails(
        {
          placeId,
          fields: [
            "geometry",
            "formatted_address",
            "address_components",
            "types",
          ],
          sessionToken: getSessionToken(),
        },
        (result, status) => {
          resetSessionToken();

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            result
          ) {
            resolve({
              formatted_address: result.formatted_address,
              address_components: result.address_components || [],
              geometry: {
                location: {
                  lat: result.geometry.location.lat(),
                  lng: result.geometry.location.lng(),
                },
              },
              types: result.types || [],
            });
          } else {
            reject(new Error("Place details failed: " + status));
          }
        }
      );
    });
  } catch (error) {
    console.warn("Place details failed:", error.message);
    return null;
  }
}
